import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { apiUpload } from '../../lib/api';

const IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
]);

function getImageFiles(dataTransfer: DataTransfer): File[] {
  const files: File[] = [];
  for (let i = 0; i < dataTransfer.files.length; i++) {
    const file = dataTransfer.files[i];
    if (IMAGE_MIME_TYPES.has(file.type)) {
      files.push(file);
    }
  }
  return files;
}

/**
 * Upload an image file to /api/images and insert the resulting URL
 * into the editor as an image node.
 */
async function uploadAndInsertImage(
  file: File,
  view: import('@tiptap/pm/view').EditorView,
  pos?: number,
): Promise<void> {
  if (!IMAGE_MIME_TYPES.has(file.type)) return;

  try {
    const formData = new FormData();
    formData.append('file', file);

    const result = await apiUpload<{ url: string }>('/images', formData);

    const { schema } = view.state;
    const imageNode = schema.nodes.image?.create({
      src: result.url,
      alt: file.name,
    });

    if (!imageNode) return;

    const insertPos = pos ?? view.state.selection.anchor;
    const tr = view.state.tr.insert(insertPos, imageNode);
    view.dispatch(tr);
  } catch (err) {
    console.error('Failed to upload image:', err);
  }
}

/**
 * TipTap extension that handles image paste and drop events by
 * uploading them to the server and inserting the URL.
 */
export const ImageUpload = Extension.create({
  name: 'imageUpload',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('imageUpload'),
        props: {
          handleDrop(view, event, _slice, moved) {
            if (moved || !event.dataTransfer) return false;

            const images = getImageFiles(event.dataTransfer);
            if (images.length === 0) return false;

            event.preventDefault();

            // Calculate drop position
            const coordinates = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            const pos = coordinates?.pos;

            for (const file of images) {
              uploadAndInsertImage(file, view, pos);
            }

            return true;
          },

          handlePaste(view, event) {
            if (!event.clipboardData) return false;

            const images = getImageFiles(event.clipboardData);
            if (images.length === 0) return false;

            event.preventDefault();

            for (const file of images) {
              uploadAndInsertImage(file, view);
            }

            return true;
          },
        },
      }),
    ];
  },
});
